import { Toast as BaseToast } from '@base-ui/react/toast'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type ToastType = 'success' | 'error' | 'info'

// A module-level manager so non-React code (the Inertia flash listener) can
// fire toasts; the provider below plugs it into the React tree.
export const toastManager = BaseToast.createToastManager()

export function showToast(options: { title?: string; description: string; type?: ToastType }) {
  toastManager.add({
    title: options.title,
    description: options.description,
    type: options.type ?? 'info',
  })
}

export const useToastManager = BaseToast.useToastManager

function ToastList() {
  const { toasts } = BaseToast.useToastManager()
  return toasts.map((toast) => (
    <BaseToast.Root
      key={toast.id}
      toast={toast}
      className={cx('toast', toast.type === 'success' && 'toast--success', toast.type === 'error' && 'toast--error')}
    >
      <div className="min-w-0 flex-1">
        {toast.title != null && <BaseToast.Title className="toast__title" />}
        <BaseToast.Description className="toast__description" />
      </div>
      <BaseToast.Close className="toast__close" aria-label="Dismiss notification">
        <X size={16} aria-hidden="true" />
      </BaseToast.Close>
    </BaseToast.Root>
  ))
}

// Wrap the app once (from the Inertia entrypoint) so any page can toast.
export function AppToastProvider({ children }: { children: ReactNode }) {
  return (
    <BaseToast.Provider toastManager={toastManager}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport className="toast-viewport">
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  )
}
